<script setup>
// NOTICE: KAPTION-INTEGRATION - WhatsApp Direct Send campaign creation page
// Creates a new direct campaign (inbox + template + title) then redirects to detail
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import KaptionMediaUpload from 'dashboard/components-next/Campaigns/KaptionMediaUpload.vue';
// NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
import KaptionCampaignAssistantSelector from 'dashboard/components/kaption/KaptionCampaignAssistantSelector.vue';
import useTemplateVariables from 'dashboard/composables/useTemplateVariables';
import { MEDIA_FORMATS } from 'dashboard/helper/templateHelper';
// NOTICE: KAPTION-INTEGRATION - Campaign skip-assistant toggle (feature-flag gated)
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const route = useRoute();
const router = useRouter();
const store = useStore();

const accountId = computed(() => route.params.accountId);

// ---------- Form state ----------
const title = ref('');
const inboxId = ref(null);
const templateId = ref(null);
const isSubmitting = ref(false);
// NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
const assistantId = ref(null);
const variableBindings = ref({});
const allVariablesMapped = ref(false);
const shortenLinks = ref(false);
// NOTICE: KAPTION-INTEGRATION - Default tappable card title (tap_target_configuration)
// stored on the wrapper's trigger_rules; applied to every send through it.
const tapTargetTitle = ref('');
// NOTICE: KAPTION-INTEGRATION - Deliver replies straight to inbox, bypassing the Kaption assistant
const skipAssistant = ref(false);
// NOTICE: KAPTION-INTEGRATION - DRY-RUN: mark this wrapper simulate-only. Sends through it emit a
// `send_attempt` event (visible in analytics) instead of delivering to customers — so the wrapper
// can be tested/unarchived safely without blasting real contacts.
const dryRun = ref(false);

// ---------- Store getters ----------
const getDirectMessageTemplates = useMapGetter(
  'inboxes/getDirectMessageTemplates'
);
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');

// ---------- Inbox options ----------
const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

// ---------- Template options ----------
// Direct-send template policy:
//   - `kaption_*` templates are reserved for system-managed flows
//     (reminders, scheduled messages, follow-ups) and are intentionally
//     hidden from this dropdown. Every other customer-managed template
//     is a first-class citizen regardless of category (UTILITY,
//     AUTHENTICATION, MARKETING).
const templateOptions = computed(() => {
  if (!inboxId.value) return [];
  const templates = getDirectMessageTemplates.value(inboxId.value);
  return (templates || [])
    .filter(tmpl => !tmpl.name.startsWith('kaption_'))
    .map(tmpl => {
      const friendlyName = tmpl.name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      const isPending = tmpl.status?.toLowerCase() === 'pending';
      const suffix = isPending ? ` - ${t('DIRECT.SEND.PENDING_BADGE')}` : '';
      return {
        value: tmpl.id,
        label: `${friendlyName} (${tmpl.language || 'en'})${suffix}`,
        template: tmpl,
      };
    });
});

// ---------- Selected template ----------
const selectedTemplate = computed(() => {
  if (!inboxId.value || !templateId.value) return null;
  const templates = getDirectMessageTemplates.value(inboxId.value);
  return templates?.find(tmpl => tmpl.id === templateId.value) || null;
});

const selectedInbox = computed(() =>
  inboxes.value?.find(i => i.id === inboxId.value)
);

// ---------- Template text + variable extraction ----------
const templateText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = selectedTemplate.value.components.find(c => c.type === 'BODY');
  return body ? body.text : '';
});

const VARIABLE_PATTERN = /{{\s*([^}]+)\s*}}/g;
const templateVariables = computed(() => {
  if (!templateText.value) return [];
  const matches = Array.from(templateText.value.matchAll(VARIABLE_PATTERN));
  return [...new Set(matches.map(m => m[1].trim()))];
});

// ---------- Header media detection ----------
const headerComponent = computed(() => {
  if (!selectedTemplate.value) return null;
  return selectedTemplate.value.components.find(c => c.type === 'HEADER');
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

// Detect URL buttons in the selected template — when present, link shortening
// is mandatory (Kaption click attribution requires kapt.io URLs).
const hasUrlButton = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttonsComp = selectedTemplate.value.components?.find(
    c => c.type === 'BUTTONS'
  );
  if (!buttonsComp) return false;
  return (buttonsComp.buttons || []).some(b => b.type === 'URL');
});

// `shortenLinks` is forced ON (and disabled) when the template has URL buttons.
const shortenLinksEffective = computed(
  () => hasUrlButton.value || shortenLinks.value
);

const mediaHeaderType = computed(
  () => headerComponent.value?.format?.toLowerCase() || ''
);

const templateExampleMediaUrl = computed(() => {
  const hdr = headerComponent.value;
  if (!hdr) return '';
  return (
    hdr.example?.header_handle?.[0] ||
    hdr.example?.header_url?.[0] ||
    hdr.example?.[0] ||
    ''
  );
});

// ---------- Variable suggestions ----------
const { variableSuggestions } = useTemplateVariables({
  sampleContacts: computed(() => []),
});

const availableBindingsForSlot = computed(() => {
  const contactBindings = (variableSuggestions.value || [])
    .filter(s => s.key?.startsWith('contact.'))
    .map(s => ({ value: `{{ ${s.key} }}`, label: s.label }));

  const workflowExamples = [
    { value: '{{ workflow.order_id }}', label: 'workflow.order_id' },
    { value: '{{ workflow.tracking_url }}', label: 'workflow.tracking_url' },
  ];

  return [...contactBindings, ...workflowExamples];
});

// ---------- Preview computeds ----------
const iframeVariables = computed(() => {
  const bindings = variableBindings.value?.body || {};
  return templateVariables.value.map(slot => ({
    slot,
    binding: bindings[slot] || null,
    availableBindings: availableBindingsForSlot.value,
  }));
});

const previewTemplate = computed(() => {
  if (!selectedTemplate.value) return null;
  const overrideUrl = variableBindings.value?.header?.media_url;
  if (!overrideUrl || !hasMediaHeader.value) return selectedTemplate.value;
  const components = selectedTemplate.value.components.map(c => {
    if (c.type !== 'HEADER') return c;
    return {
      ...c,
      example: { ...c.example, header_handle: [overrideUrl] },
    };
  });
  return { ...selectedTemplate.value, components };
});

// ---------- Binding handlers ----------
const checkAllVariablesMapped = () => {
  if (templateVariables.value.length === 0) {
    allVariablesMapped.value = true;
    return;
  }
  const bodyBindings = variableBindings.value?.body || {};
  allVariablesMapped.value = templateVariables.value.every(
    varKey => bodyBindings[varKey] && bodyBindings[varKey].trim() !== ''
  );
};

const handleBindingsUpdate = bindings => {
  const newBody = {};
  bindings.forEach(({ slot, binding }) => {
    if (binding) newBody[slot] = binding;
  });
  variableBindings.value = {
    ...variableBindings.value,
    body: newBody,
  };
  checkAllVariablesMapped();
};

const updateHeaderMediaUrl = value => {
  variableBindings.value = {
    ...variableBindings.value,
    header: { media_url: value, media_type: mediaHeaderType.value },
  };
};

// ---------- Auto-generate title from template name ----------
const autoTitle = computed(() => {
  if (!selectedTemplate.value) return '';
  return selectedTemplate.value.name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
});

// Track whether user manually edited the title
const titleManuallyEdited = ref(false);

const handleTitleInput = () => {
  titleManuallyEdited.value = true;
};

// Reset template when inbox changes
watch(
  () => inboxId.value,
  () => {
    templateId.value = null;
  }
);

// Auto-fill title + reset bindings when template changes
watch(
  () => templateId.value,
  newId => {
    if (!titleManuallyEdited.value && autoTitle.value) {
      title.value = autoTitle.value;
    }
    const newBindings = {};
    if (newId && hasMediaHeader.value && templateExampleMediaUrl.value) {
      newBindings.header = {
        media_url: templateExampleMediaUrl.value,
        media_type: mediaHeaderType.value,
      };
    }
    variableBindings.value = newBindings;
    allVariablesMapped.value = false;
    checkAllVariablesMapped();
  }
);

// ---------- Validation ----------
const isFormValid = computed(() => {
  const hasTitle = title.value.trim().length > 0;
  const hasInbox = !!inboxId.value;
  const hasTemplate = !!templateId.value;
  const variablesOk =
    templateVariables.value.length === 0 || allVariablesMapped.value;
  return hasTitle && hasInbox && hasTemplate && variablesOk;
});

// ---------- Submit ----------
const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    const template = selectedTemplate.value;

    // Build processed_params from bindings
    const processedParams = {};
    const bodyBindings = variableBindings.value?.body;
    if (bodyBindings && Object.keys(bodyBindings).length > 0) {
      processedParams.body = {};
      Object.entries(bodyBindings).forEach(([key, value]) => {
        const cleanKey = key.replace(/^{{|}}$/g, '').trim();
        processedParams.body[cleanKey] = value;
      });
    }
    if (variableBindings.value?.header) {
      processedParams.header = { ...variableBindings.value.header };
    }

    const payload = {
      title: title.value.trim(),
      inbox_id: inboxId.value,
      template_params: {
        name: template.name,
        language: template.language,
        namespace: template.namespace || '',
        processed_params:
          Object.keys(processedParams).length > 0 ? processedParams : undefined,
        shorten_links: shortenLinksEffective.value,
      },
      // NOTICE: KAPTION-INTEGRATION - Skip-assistant delivery (open + unassigned)
      trigger_rules: {
        skip_assistant: skipAssistant.value,
        // NOTICE: KAPTION-INTEGRATION - Default card title for this wrapper's sends
        tap_target_title: tapTargetTitle.value?.trim() || null,
        // NOTICE: KAPTION-INTEGRATION - DRY-RUN: when true, sends through this wrapper are
        // simulated (send_attempt event, no Meta delivery). Re-checked at send time.
        dry_run: dryRun.value,
      },
      // NOTICE: KAPTION-INTEGRATION - Campaign assistant routing
      ...(assistantId.value && {
        kaption_assistant_id: assistantId.value,
      }),
    };

    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/direct_messages`,
      payload
    );

    useAlert(t('DIRECT.CREATE.SUCCESS'));
    router.push({
      name: 'campaigns_direct_detail',
      params: { directId: response.data.id },
    });
  } catch (err) {
    const apiError = err?.response?.data?.error;
    if (apiError?.includes('Invalid variable bindings')) {
      useAlert(t('DIRECT.CREATE.INVALID_BINDINGS', { errors: apiError }));
    } else {
      useAlert(t('DIRECT.CREATE.ERROR'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push({ name: 'campaigns_direct_index' });
};

const navigateToCreateTemplate = () => {
  router.push({
    name: 'campaigns_whatsapp_templates_new',
    query: { returnTo: 'direct_create', inboxId: inboxId.value },
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
  const qInboxId = route.query.inboxId;
  if (qInboxId) inboxId.value = Number(qInboxId);
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background -mx-6">
    <!-- Header -->
    <header
      class="relative flex items-center px-8 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
        @click="handleCancel"
      >
        <span class="i-lucide-arrow-left w-5 h-5" />
        <span class="text-sm font-medium">{{ t('DIRECT.SEND.BACK') }}</span>
      </button>
      <h1
        class="absolute left-1/2 -translate-x-1/2 text-lg font-semibold text-n-slate-12"
      >
        {{ t('DIRECT.CREATE.TITLE') }}
      </h1>
    </header>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto px-8 py-6">
      <div class="max-w-5xl mx-auto grid grid-cols-5 gap-6">
        <!-- Left Column: Form (3/5) -->
        <div class="col-span-3 flex flex-col gap-6 pr-2">
          <!-- Inbox Selection -->
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.INBOX_LABEL') }}
            </label>
            <ComboBox
              v-model="inboxId"
              :options="inboxOptions"
              :placeholder="t('DIRECT.SEND.INBOX_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
          </div>

          <!-- Template Selection -->
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.TEMPLATE_LABEL') }}
            </label>
            <ComboBox
              v-model="templateId"
              :options="templateOptions"
              :placeholder="t('DIRECT.SEND.TEMPLATE_PLACEHOLDER')"
              :disabled="!inboxId"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />
            <button
              type="button"
              class="text-xs text-n-brand hover:text-n-brand-hover flex items-center gap-1 mt-1"
              @click="navigateToCreateTemplate"
            >
              <span class="i-lucide-plus w-3 h-3" />
              {{ t('DIRECT.CREATE_TEMPLATE') }}
            </button>
          </div>

          <!-- Title -->
          <div class="flex flex-col gap-1">
            <Input
              v-model="title"
              :label="t('DIRECT.CREATE.TITLE_LABEL')"
              :placeholder="t('DIRECT.CREATE.TITLE_PLACEHOLDER')"
              @input="handleTitleInput"
            />
          </div>

          <!-- Shorten links via kapt.io -->
          <div v-if="selectedTemplate" class="flex flex-col gap-1">
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="shortenLinksEffective"
                :disabled="hasUrlButton"
                class="h-4 w-4 rounded border-n-weak"
                @change="shortenLinks = $event.target.checked"
              />
              {{ t('DIRECT.SEND.SHORTEN_LINKS_LABEL') }}
            </label>
            <p class="text-xs text-n-slate-11 ml-6">
              <template v-if="hasUrlButton">
                {{ t('DIRECT.SEND.SHORTEN_LINKS_FORCED_HINT') }}
              </template>
              <template v-else>
                {{ t('DIRECT.SEND.SHORTEN_LINKS_HINT') }}
              </template>
            </p>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Default card title (tap_target_configuration).
               Only shown for templates with a URL button (backend no-ops otherwise). -->
          <div v-if="hasUrlButton" class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('DIRECT.SEND.TAP_TARGET_TITLE_LABEL') }}
            </label>
            <Input
              v-model="tapTargetTitle"
              type="text"
              :placeholder="t('DIRECT.SEND.TAP_TARGET_TITLE_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-11">
              {{ t('DIRECT.SEND.TAP_TARGET_TITLE_HINT') }}
            </p>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Deliver straight to inbox (skip assistant) -->
          <div
            v-if="isKaptionFlagEnabled('KAPTION_CAMPAIGN_SKIP_ASSISTANT')"
            class="flex flex-col gap-1"
          >
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="skipAssistant"
                class="h-4 w-4 rounded border-n-weak"
                @change="skipAssistant = $event.target.checked"
              />
              {{ t('DIRECT.SEND.SKIP_ASSISTANT_LABEL') }}
            </label>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ t('DIRECT.SEND.SKIP_ASSISTANT_HINT') }}
            </p>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - DRY-RUN: simulate sends (no delivery) -->
          <div class="flex flex-col gap-1">
            <label
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="dryRun"
                class="h-4 w-4 rounded border-n-weak"
                @change="dryRun = $event.target.checked"
              />
              {{ t('DIRECT.SEND.DRY_RUN_LABEL') }}
            </label>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ t('DIRECT.SEND.DRY_RUN_HINT') }}
            </p>
          </div>

          <!-- NOTICE: KAPTION-INTEGRATION - Campaign assistant selector -->
          <KaptionCampaignAssistantSelector v-model="assistantId" />
        </div>

        <!-- Right Column: Preview (2/5) -->
        <div class="col-span-2 flex flex-col gap-3">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('DIRECT.SEND.PREVIEW_TITLE') }}
          </h3>
          <KaptionMediaUpload
            v-if="hasMediaHeader && selectedTemplate"
            :model-value="variableBindings?.header?.media_url || ''"
            :media-type="mediaHeaderType"
            @update:model-value="updateHeaderMediaUrl"
          />
          <KaptionWhatsAppPreviewEnhanced
            v-if="selectedTemplate"
            :template="previewTemplate"
            :variables="iframeVariables"
            :resolved-values="{}"
            :tap-target-title="tapTargetTitle"
            :mode="templateVariables.length > 0 ? 'edit' : 'render'"
            :loading="false"
            :bare="false"
            :company-name="selectedInbox?.name || 'Business'"
            class="w-full h-[520px]"
            @bindings-update="handleBindingsUpdate"
          />
          <KaptionWhatsAppPreviewEnhanced
            v-else
            :template="null"
            :loading="false"
            :bare="false"
            mode="render"
            class="w-full h-[520px]"
          />
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="flex items-center justify-between px-8 py-4 border-t border-n-weak bg-n-solid-2"
    >
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:text-n-slate-12 px-4 py-2.5 rounded-lg hover:bg-n-alpha-black2 transition-colors"
        @click="handleCancel"
      >
        {{ t('DIALOG.BUTTONS.CANCEL') }}
      </button>
      <button
        type="button"
        class="px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        :class="[
          isFormValid
            ? 'text-white bg-n-brand hover:bg-n-brand-hover shadow-sm'
            : 'bg-n-slate-3 text-n-slate-10 cursor-not-allowed',
        ]"
        :disabled="!isFormValid || isSubmitting"
        @click="handleSubmit"
      >
        <span
          v-if="isSubmitting"
          class="animate-spin h-4 w-4 border-2 border-white border-r-transparent rounded-full"
        />
        <span class="i-lucide-plus w-4 h-4" />
        {{ t('DIRECT.CREATE.CREATE_BUTTON') }}
      </button>
    </footer>
  </div>
</template>
